import { useEffect, useState } from 'react';
import { ToolResultPageRenderer } from '@/components/tools/ToolResultPageRenderer';
import { buildRunResultTabs, type ToolResultTab } from '@/lib/tool-result-composition';
import { parseResultPayload } from '@shared/types/result-composition';
import { isViewType, type AnyViewEnvelope } from '@shared/types/views';
import { supabase } from '@/lib/supabase';
import { cn } from '@/lib/utils';
import { IN_SURFACE_CLASS } from '@/lib/views';

/**
 * The full result of the run behind an object (e.g. the analysed call),
 * rendered like a run. Readable by the app's members since F5d; otherwise the
 * page says it stays with its author.
 */
export const AppRunResult = ({ runId, title }: { runId: string; title: string }) => {
  const [result, setResult] = useState<{ runId: string; tabs: ToolResultTab[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [attempt, setAttempt] = useState(0);
  const [tabId, setTabId] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setResult(null);
    setError(null);
    setLoading(true);
    setTabId(null);
    void (async () => {
      const { data, error: readError } = await supabase.from('flow_executions')
        .select('result_payload, view_payload').eq('id', runId).maybeSingle();
      if (readError) throw readError;
      if (!active || !data) return;
      const payload = parseResultPayload(data.result_payload);
      if (data.result_payload != null && !payload) throw new Error('invalid_result_payload');
      const view = data.view_payload as AnyViewEnvelope | null;
      const fallback = view && isViewType(view.view_type) && view.data && typeof view.data === 'object'
        ? { viewType: view.view_type, envelope: view } : null;
      setResult({ runId, tabs: buildRunResultTabs({ title, resultPayload: payload, fallback }).filter((tab) => tab.scope === 'run') });
    })().catch(() => { if (active) setError('Le résultat n’a pas pu être chargé.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [runId, title, attempt]);

  const current = result?.runId === runId ? result : null;
  const tabs = current?.tabs ?? [];
  if (loading) return <p role="status" className="text-sm text-domino-inkSoft">Chargement du résultat…</p>;
  if (error) return <div role="alert" className="text-sm text-[var(--domino-critical)]">
    <p>{error}</p>
    <button type="button" onClick={() => setAttempt((value) => value + 1)} className="mt-2 underline">Réessayer</button>
  </div>;
  if (!current) return <p className="text-sm text-domino-inkSoft">Résultat introuvable ou inaccessible.</p>;
  if (tabs.length === 0) return <p className="text-sm text-domino-inkSoft">Cette exécution n’a pas de résultat détaillé.</p>;
  const active = tabs.find((tab) => tab.id === tabId) ?? tabs[0]!;
  return (
    <div className={`overflow-hidden rounded-2xl border border-domino-cardBorder bg-domino-white ${IN_SURFACE_CLASS}`}>
      {tabs.length > 1 && (
        <div role="tablist" aria-label="Résultat" className="flex flex-wrap gap-1 border-b border-domino-cardBorder px-4">
          {tabs.map((tab) => (
            <button key={tab.id} type="button" role="tab" aria-selected={tab.id === active.id} onClick={() => setTabId(tab.id)}
              className={cn('border-b-2 px-3 py-2 text-sm font-semibold', tab.id === active.id ? 'border-domino-coral text-domino-ink' : 'border-transparent text-domino-inkSoft hover:text-domino-ink')}>
              {tab.label}
            </button>
          ))}
        </div>
      )}
      <div className="pt-5">
        <ToolResultPageRenderer composition={active.composition} viewEnvelopesByInstanceId={active.viewEnvelopesByInstanceId}
          documentOutputs={active.documentOutputs} pageMode="artifact" pageTitle={title} />
      </div>
    </div>
  );
};
